<template>
    <div>
        <dialog
            ref="dialog-ref"
            class="p-md top-1/2 left-1/2 w-96 -translate-x-1/2 -translate-y-1/2 rounded-md bg-(--overlay-surface) shadow-md"
        >
            <template v-if="canApprove">
                <h2>Decline the request</h2>
                <p class="my-sm">
                    Are you sure you want to decline
                    {{ pendingDecline.length > 1 ? pendingDecline.length : 'the' }} user
                    {{ pendingDecline.length > 1 ? 'invitations' : 'invitation' }}? This action cannot be undone, and
                    the {{ pendingDecline.length > 1 ? 'users' : 'user' }} will need to be invited again if you change
                    your mind later.
                </p>
                <label for="decline-comment" class="mb-xs block text-sm font-medium">
                    Comment <span class="text-(--text-muted)">(optional)</span>
                </label>
                <textarea
                    id="decline-comment"
                    v-model="declineComment"
                    rows="3"
                    maxlength="2000"
                    class="mb-sm w-full rounded-md border border-(--border-strong) p-2 text-sm"
                    placeholder="Add a note for the requester. This will be included in the notification email."
                />
                <div class="gap-sm flex">
                    <Button class="bg-(--btn-danger-bg) text-(--btn-danger-fg) hover:bg-(--btn-danger-hover-bg)" @click="confirmDecline"> Decline </Button>
                    <button class="btn-link" @click="cancelDecline">Cancel</button>
                </div>
            </template>
            <h2 v-else>You should not be able to see this?</h2>
        </dialog>

        <table class="table w-full">
            <thead>
                <tr>
                    <th v-if="canApprove">
                        <input id="select-all" type="checkbox" name="select-all" @click="selectAll" />
                    </th>
                    <th>
                        <SortToggle
                            :field-name="'status'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >Status</SortToggle
                        >
                    </th>
                    <th>
                        <SortToggle
                            :field-name="'first_name'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >Name</SortToggle
                        >
                    </th>
                    <th>
                        <SortToggle
                            :field-name="'email'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >Email</SortToggle
                        >
                    </th>
                    <th>
                        <SortToggle
                            :field-name="'organization.name'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >InvitedTo</SortToggle
                        >
                    </th>
                    <th>
                        <SortToggle
                            :field-name="'creator.EMAIL'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >Invited by</SortToggle
                        >
                    </th>
                    <th>Groups</th>
                    <th>Role</th>
                    <th v-if="canApprove" class="[&>div]:flex [&>div]:justify-end">
                        <SortToggle
                            :field-name="'status'"
                            :sort-column="sortColumn"
                            :sort-direction="sortDirection"
                            @sort="swapSort"
                            >Action</SortToggle
                        >
                    </th>
                </tr>
            </thead>
            <tbody>
                <SkeletonTable v-if="isLoading" :column-count="canApprove ? 9 : 7" />
                <tr v-for="user in sortedUsers" :key="user.user_invitation_id" class="relative">
                    <td v-if="canApprove">
                        <input
                            v-model="selectedUsers"
                            type="checkbox"
                            name="select"
                            :value="user.user_invitation_id"
                            class="h-10"
                            :disabled="user.status !== 'PENDING'"
                        />
                    </td>
                    <td>
                        <div class="gap-xs flex items-center" :class="classCollection(user.status)">
                            <span class="mx-xs inline-block size-2 rounded-full" />
                            {{ user.status }}
                        </div>
                    </td>
                    <td>{{ `${user.first_name} ${user.last_name}` }}</td>
                    <td>{{ user?.email }}</td>
                    <td>{{ user?.organization?.name }}</td>
                    <td>{{ user?.creator?.EMAIL ?? user?.created_by ?? '-' }}</td>
                    <td>{{ formatNames(user.groups) || '-' }}</td>
                    <td class="whitespace-nowrap">
                        <UserRoleTagComponent :org-admin="invitationOrgAdminFlag(user.roles)" />
                    </td>
                    <td v-if="canApprove">
                        <div class="gap-sm flex justify-end">
                            <template v-if="selectedUsers.length < 2 && user.status === 'PENDING'">
                                <Button
                                    class="border border-(--state-danger-border) bg-(--surface-1) text-(--state-danger-fg) transition-all hover:border-(--btn-danger-hover-bg) hover:bg-(--btn-danger-hover-bg) hover:text-(--btn-danger-fg)"
                                    @click="activateModal([user.user_invitation_id])"
                                >
                                    Decline
                                </Button>
                                <Button
                                    class="flex border border-(--btn-outline-fg) bg-(--surface-1) text-(--link-default) transition-all hover:bg-(--btn-primary-bg) hover:text-(--text-on-accent)"
                                    @click="$emit('approve-users', [user.user_invitation_id])"
                                >
                                    Approve <i class="i-checkmark" />
                                </Button>
                            </template>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- @cancel="activateModal(selectedUsers)"
    cancel-text="Decline" -->
        <BottomButtons
            v-if="selectedUsers.length >= 2"
            :handle-submit="() => $emit('approve-users', selectedUsers)"
            full-width
        />
    </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import SkeletonTable from '@/components/skeleton-table.vue';
import SortToggle from '@/components/admin/sort-toggle.vue';
import type { AdminUserInvitation } from '@/stores/store-interfaces';
import { computed, ref, useTemplateRef, type PropType } from 'vue';
import { invitationOrgAdminFlag, tableSearchAndSort } from '@/stores/helpers';
import BottomButtons from '@/components/bottom-buttons.vue';
import UserRoleTagComponent from '@/components/ui/tag/UserRoleTagComponent.vue';

const sortColumn = ref<keyof AdminUserInvitation>('status');
const sortDirection = ref<boolean>(false);
const selectedUsers = ref<Array<number>>([]);
const pendingDecline = ref<Array<number>>([]);
const declineComment = ref<string>('');

const isLoading = computed(() => {
    return !props.users;
});

const dialog = useTemplateRef<HTMLDialogElement>('dialog-ref');

const props = defineProps({
    users: { type: Array as PropType<AdminUserInvitation[]>, required: true },
    searchInput: { type: String, required: true },
    canApprove: { type: Boolean, required: true },
});

const emit = defineEmits(['approve-users', 'decline-users']);

const sortedUsers = computed(() => {
    const sorted = tableSearchAndSort(props.users, props.searchInput, sortColumn.value, sortDirection.value);
    return sorted;
});

const selectAll = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
        selectedUsers.value = sortedUsers.value
            .filter((user) => user.status === 'PENDING')
            .map((user) => user.user_invitation_id);
    } else {
        selectedUsers.value = [];
    }
};

const swapSort = (sortName: keyof AdminUserInvitation) => {
    if (sortColumn.value === sortName) {
        sortDirection.value = !sortDirection.value;
    } else {
        sortColumn.value = sortName;
        sortDirection.value = true;
    }
};

const classCollection = (status: string) => {
    switch (status) {
        case 'PENDING':
            return 'tag-info text-(--state-info-fg) [&>span]:bg-(--state-info-fg)';
        case 'WAITING FOR APPROVAL':
            return 'tag-hold [&>span]:bg-(--state-warning-fg) text-(--state-warning-fg)';
        case 'APPROVED':
            return 'text-(--state-success-fg) [&>span]:bg-(--state-success-fg)';
        case 'DECLINED':
            return 'text-(--state-danger-fg) [&>span]:bg-(--state-danger-fg)';
        case 'CANCELLED':
            return 'text-(--text-muted) [&>span]:bg-(--icon-secondary)';
    }
};

// Deleted groups/roles come back with a null name; show the id so the row still explains itself.
const formatNames = (selections: AdminUserInvitation['groups'] | AdminUserInvitation['roles']): string =>
    (selections ?? []).map((selection) => selection.name ?? `#${selection.id}`).join(', ');

const activateModal = (ids: number[]) => {
    if (!ids.length || !dialog.value) return;
    pendingDecline.value = ids;
    declineComment.value = '';
    dialog.value.showModal();
};

const confirmDecline = () => {
    emit('decline-users', pendingDecline.value, declineComment.value.trim() || undefined);
    dialog.value?.close();
    pendingDecline.value = [];
    declineComment.value = '';
};

const cancelDecline = () => {
    dialog.value?.close();
    pendingDecline.value = [];
    declineComment.value = '';
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';

th:nth-of-type(1) {
    width: 2.5%;
}
th:nth-of-type(7) {
    width: 10%;
}
th:nth-of-type(2),
th:nth-of-type(3),
th:nth-of-type(4) {
    width: 17.5%;
}
th:nth-of-type(5),
th:nth-of-type(6) {
    width: 15%;
}
/* Role — wide enough for the org-admin / org-user tag to stay on one line. */
th:nth-of-type(8) {
    width: 8%;
}
</style>
